import { useCampaignContext } from "../CampaignContext";
import { useSelector } from 'react-redux';
import { RootState } from '@/src/State/store';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { detectNonEnglishContent } from "../utils/detectNonEnglishContent";

export const useCampaignValidators = () => {
  const {
    setCampaignNameError,
    setFromCountryError,
    setToCountryError,
    setContentError,
    setStartDateError,
    setEndDateError,
    setStartTimeError,
    setEndTimeError,
    setLanguageError,
    setPhoneNumberError,
    setWhatsappBodyError,
    whatsappBodyError,
    setSmsBodyError,
    smsBodyError,
    campaignStartDate,
    campaignEndDate,
    startTime,
    endTime,
    setTimeZoneError,
    // New channel validation states from context
    countValidationAgainstEachCountry,
    setCountValidationAgainstEachCountry,
    minLimit,
    setMinLimit,
    maxLimit,
    setMaxLimit,
    setCampaignNames,
    campaignNames,
    setHeaderTextError,
    setWhatsappFooterError,
    buttonValidationErrors,
    setButtonValidationErrors,
    setButtonsError,
    setButtonError,
    getButtonError,
    clearButtonErrors,
    language,
    setDraftNameError
  } = useCampaignContext();

  // Get API URL and email from Redux (for API calls)

  const accountEmail = useSelector((state: RootState) => state.authentication.userEmail);
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
  const workspaceId = useSelector((state: RootState) => state.authentication.workspace_id);

  const isMember = useSelector((state: RootState) => state.authentication.isInvited)



  /**
   * Helper function to set individual field error
   */
  const setCountError = (channel: 'whatsapp' | 'sms', index: number, error: string | null) => {
    setCountValidationAgainstEachCountry(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        ...(error ? { [index]: error } : (() => {
          const { [index]: removed, ...rest } = prev[channel];
          return rest;
        })())
      }
    }));
  };

  /**
   * Helper function to clear individual field error
   */
  const clearCountError = (channel: 'whatsapp' | 'sms', index: number) => {
    setCountValidationAgainstEachCountry(prev => {
      const { [index]: removed, ...rest } = prev[channel];
      return {
        ...prev,
        [channel]: rest
      };
    });
  };

  /**
   * Helper function to get error for specific field
   */
  const getCountError = (channel: 'whatsapp' | 'sms', index: number): string | undefined => {
    return countValidationAgainstEachCountry[channel]?.[index];
  };

  /**
   * Validates traveller count for a specific field:
   * - Required (non-empty after trimming)
   * - Must be a valid number
   * - Must be an integer (whole number)
   * - Must be positive (greater than 0)
   * - Must be within min/max limits if they exist
   */
  const validateTravellerCount = (value: string, channel: 'whatsapp' | 'sms', index: number): boolean => {
    const trimmedValue = value.trim();




    // ✅ If no value entered → skip validation & clear errors
    if (!trimmedValue) {
      clearCountError(channel, index);
      return true;
    }

    // Check if it's a valid number
    const numValue = Number(trimmedValue);
    if (isNaN(numValue)) {
      setCountError(channel, index, "Please enter a valid number");
      return false;
    }

    // Check if it's an integer
    if (!Number.isInteger(numValue)) {
      setCountError(channel, index, "Please enter a whole number");
      return false;
    }


    // Check min limit if exists
    if (minLimit && numValue < parseInt(minLimit)) {
      setCountError(channel, index, `Minimum ${minLimit} travellers required`);
      return false;
    }

    // Check max limit if exists
    if (maxLimit && numValue > parseInt(maxLimit)) {
      setCountError(channel, index, `Maximum ${maxLimit} travellers allowed`);
      return false;
    }

    // All validations passed - clear any existing error
    clearCountError(channel, index);
    return true;
  };

  /**
   * Validates all traveller count fields for a specific channel:
   * - Checks each non-empty field in the channel data
   * - Returns true only if all fields are valid
   */
  const validateChannelTravellerCounts = (channel: 'whatsapp' | 'sms', data: Array<{ country: string; count: string }>): boolean => {
    let allValid = true;

    data.forEach((item, index) => {
      // Only validate fields that have values (non-empty)
      if (item.count.trim() !== "") {
        const isValid = validateTravellerCount(item.count, channel, index);
        if (!isValid) {
          allValid = false;
        }
      } else {
        // Clear error for empty fields (they're optional until user starts typing)
        clearCountError(channel, index);
      }
    });

    return allValid;
  };

  /**
   * Validates all channel data (both WhatsApp and SMS):
   * - Validates all traveller count fields across both channels
   * - Returns true only if all fields are valid
   */
  const validateAllChannelData = (
    whatsappData: Array<{ country: string; count: string }>,
    smsData: Array<{ country: string; count: string }>
  ): boolean => {
    const whatsappValid = validateChannelTravellerCounts('whatsapp', whatsappData);
    const smsValid = validateChannelTravellerCounts('sms', smsData);

    return whatsappValid && smsValid;
  };

  /**
   * Clears all validation errors for a specific channel:
   * - Useful when switching channels or resetting form
   */
  const clearChannelErrors = (channel: 'whatsapp' | 'sms') => {
    setCountValidationAgainstEachCountry(prev => ({
      ...prev,
      [channel]: {}
    }));
  };

  /**
   * Clears all channel validation errors:
   * - Useful for form reset
   */
  const clearAllChannelErrors = () => {
    setCountValidationAgainstEachCountry({
      whatsapp: {},
      sms: {}
    });
  };

  /**
   * Check if any channel has validation errors:
   * - Useful for form submission validation
   */
  const hasChannelErrors = (): boolean => {
    const whatsappErrors = Object.keys(countValidationAgainstEachCountry.whatsapp).length > 0;
    const smsErrors = Object.keys(countValidationAgainstEachCountry.sms).length > 0;
    return whatsappErrors || smsErrors;
  };

  /**
   * Function to validate draft data silently when loading (for draft functionality)
   */
  // const validateDraftDataSilently = (
  //   whatsappData: Array<{ country: string; count: string }>,
  //   smsData: Array<{ country: string; count: string }>
  // ) => {
  //   // This runs validation but doesn't show errors until user interaction
  //   // Useful for pre-validating loaded draft data
  //   const errors = {
  //     whatsapp: {} as { [index: number]: string },
  //     sms: {} as { [index: number]: string }
  //   };

  //   // Validate WhatsApp data silently
  //   whatsappData.forEach((item, index) => {
  //     if (item.count.trim() !== "") {
  //       const trimmedValue = item.count.trim();
  //       const numValue = Number(trimmedValue);

  //       if (!trimmedValue) {
  //         errors.whatsapp[index] = "This field is required";
  //       } else if (isNaN(numValue)) {
  //         errors.whatsapp[index] = "Please enter a valid number";
  //       } else if (!Number.isInteger(numValue)) {
  //         errors.whatsapp[index] = "Please enter a whole number";
  //       } else if (numValue <= 0) {
  //         errors.whatsapp[index] = "Number must be greater than 0";
  //       } else if (minLimit && numValue < parseInt(minLimit)) {
  //         errors.whatsapp[index] = `Minimum ${minLimit} travellers required`;
  //       } else if (maxLimit && numValue > parseInt(maxLimit)) {
  //         errors.whatsapp[index] = `Maximum ${maxLimit} travellers allowed`;
  //       }
  //     }
  //   });

  //   // Validate SMS data silently
  //   smsData.forEach((item, index) => {
  //     if (item.count.trim() !== "") {
  //       const trimmedValue = item.count.trim();
  //       const numValue = Number(trimmedValue);

  //       if (!trimmedValue) {
  //         errors.sms[index] = "This field is required";
  //       } else if (isNaN(numValue)) {
  //         errors.sms[index] = "Please enter a valid number";
  //       } else if (!Number.isInteger(numValue)) {
  //         errors.sms[index] = "Please enter a whole number";
  //       } else if (numValue <= 0) {
  //         errors.sms[index] = "Number must be greater than 0";
  //       } else if (minLimit && numValue < parseInt(minLimit)) {
  //         errors.sms[index] = `Minimum ${minLimit} travellers required`;
  //       } else if (maxLimit && numValue > parseInt(maxLimit)) {
  //         errors.sms[index] = `Maximum ${maxLimit} travellers allowed`;
  //       }
  //     }
  //   });

  //   return errors;
  // };

  /**
   * Validates the campaign name based on the following rules:
   * - Required (non-empty after trimming)
   * - Must be between 3 and 30 characters
   * - Cannot start or end with special characters
   * - Must use only lowercase letters
   * - Only underscores allowed as special characters
   * - Cannot start with a number
   */
  const validateCampaignName = (name: string): boolean => {
    const trimmedName = name.trim();
    const minLength = 3;
    const maxLength = 30;
    const specialCharAtEdgesPattern = /^[^a-zA-Z0-9]|[^a-zA-Z0-9]$/;
    const validCharsPattern = /^[a-zA-Z0-9_ ]+$/; // added space
    const startsWithNumberPattern = /^[0-9]/;

    if (!trimmedName) {
      setCampaignNameError("Campaign name is required.");
      return false;
    }

    // Check for duplicate (case-insensitive)
    if (campaignNames.includes(trimmedName.toLowerCase())) {
      setCampaignNameError("Campaign name already exists.");
      return false;
    }

    if (trimmedName.length < minLength) {
      setCampaignNameError(`Campaign name must be at least ${minLength} characters long.`);
      return false;
    }

    if (trimmedName.length > maxLength) {
      setCampaignNameError(`Campaign name must not exceed ${maxLength} characters.`);
      return false;
    }

    if (specialCharAtEdgesPattern.test(trimmedName)) {
      setCampaignNameError("Campaign name should not start or end with a special character.");
      return false;
    }

    if (!validCharsPattern.test(trimmedName)) {
      setCampaignNameError("Only letters, numbers, and underscores are allowed.");
      return false;
    }

    if (startsWithNumberPattern.test(trimmedName)) {
      setCampaignNameError("Campaign name cannot start with a number.");
      return false;
    }

    setCampaignNameError(null);
    return true;
  };


  /**
   * Validates selected source countries:
   * - Must contain at least one selected country
   */
  const validateFromCountry = (values: string[]): boolean => {
    if (values.length === 0) {
      setFromCountryError("Please select a country.");
      return false;
    }
    setFromCountryError(null);
    return true;
  };

  /**
   * Validates selected destination country:
   * - Cannot be null or empty
   */
  const validateToCountry = (toCountry: string | null): boolean => {
    if (!toCountry || toCountry.trim() === "") {
      setToCountryError("Please select the roaming country.");
      return false;
    }
    setToCountryError(null);
    return true;
  };
  /**
   * Validates message content for WhatsApp and SMS:
   * - At least one of them must be non-empty
   */
  //Validate Content Alone for Update Campaign
  const validateContentAlone = (whatsappContent: any, smsContent: any): boolean => {
    if (!whatsappContent && !smsContent) {
      setContentError("Message content is required.");
      return false;
    }
    setContentError(null);
    return true;
  };


  //Validate Content for specific channel if count has been entered
  const validateContent = (
    whatsappContent: any,
    smsContent: any,
    channelCounts: any
  ): boolean => {
    if (!whatsappContent && !smsContent) {
      setContentError("Message content is required.");
      return false;
    }

    // Check if WhatsApp is required but missing
    if (channelCounts.whatsapp_count.length > 0 && !whatsappContent) {
      setContentError("WhatsApp content is required because traveller count is entered for sending messages via WhatsApp");
      return false;
    }

    // Check if SMS is required but missing
    if (channelCounts.sms_count.length > 0 && !smsContent) {
      setContentError("SMS content is required because traveller count is entered for sending messages via SMS.");
      return false;
    }


    if (channelCounts.sms_count.length === 0 && channelCounts.whatsapp_count.length === 0) {
      setContentError("A traveller count must be provided for at least one country");
      return false;
    }

    setContentError(null);
    return true;
  };



  // // Non-English scripts detection (for quick filtering)
  // const NON_LATIN_SCRIPTS = /[\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0D80-\u0DFF\u0E00-\u0E7F\u0E80-\u0EFF\u0F00-\u0FFF\u1000-\u109F\u10A0-\u10FF\u1100-\u11FF\u1200-\u137F\u13A0-\u13FF\u1400-\u167F\u1680-\u169F\u16A0-\u16FF\u1700-\u171F\u1720-\u173F\u1740-\u175F\u1760-\u177F\u1780-\u17FF\u1800-\u18AF\u1900-\u194F\u1950-\u197F\u1980-\u19DF\u19E0-\u19FF\u1A00-\u1A1F\u1A20-\u1AAF\u1AB0-\u1AFF\u1B00-\u1B7F\u1B80-\u1BBF\u1BC0-\u1BFF\u1C00-\u1C4F\u1C50-\u1C7F\u1C80-\u1C8F\u1CC0-\u1CCF\u1CD0-\u1CFF\u1D00-\u1D7F\u1D80-\u1DBF\u1DC0-\u1DFF\u1E00-\u1EFF\u1F00-\u1FFF\u2000-\u206F\u2070-\u209F\u20A0-\u20CF\u20D0-\u20FF\u2100-\u214F\u2150-\u218F\u2190-\u21FF\u2200-\u22FF\u2300-\u23FF\u2400-\u243F\u2440-\u245F\u2460-\u24FF\u2500-\u257F\u2580-\u259F\u25A0-\u25FF\u2600-\u26FF\u2700-\u27BF\u27C0-\u27EF\u27F0-\u27FF\u2800-\u28FF\u2900-\u297F\u2980-\u29FF\u2A00-\u2AFF\u2B00-\u2BFF\u2C00-\u2C5F\u2C60-\u2C7F\u2C80-\u2CFF\u2D00-\u2D2F\u2D30-\u2D7F\u2D80-\u2DDF\u2DE0-\u2DFF\u2E00-\u2E7F\u2E80-\u2EFF\u2F00-\u2FDF\u2FF0-\u2FFF\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\u3100-\u312F\u3130-\u318F\u3190-\u319F\u31A0-\u31BF\u31C0-\u31EF\u31F0-\u31FF\u3200-\u32FF\u3300-\u33FF\u3400-\u4DBF\u4DC0-\u4DFF\u4E00-\u9FFF\uA000-\uA48F\uA490-\uA4CF\uA4D0-\uA4FF\uA500-\uA63F\uA640-\uA69F\uA6A0-\uA6FF\uA700-\uA71F\uA720-\uA7FF\uA800-\uA82F\uA830-\uA83F\uA840-\uA87F\uA880-\uA8DF\uA8E0-\uA8FF\uA900-\uA92F\uA930-\uA95F\uA960-\uA97F\uA980-\uA9DF\uA9E0-\uA9FF\uAA00-\uAA5F\uAA60-\uAA7F\uAA80-\uAADF\uAAE0-\uAAFF\uAB00-\uAB2F\uAB30-\uAB6F\uAB70-\uABBF\uABC0-\uABFF\uAC00-\uD7AF\uD7B0-\uD7FF\uD800-\uDB7F\uDB80-\uDBFF\uDC00-\uDFFF\uE000-\uF8FF\uF900-\uFAFF\uFB00-\uFB4F\uFB50-\uFDFF\uFE00-\uFE0F\uFE10-\uFE1F\uFE20-\uFE2F\uFE30-\uFE4F\uFE50-\uFE6F\uFE70-\uFEFF\uFF00-\uFFEF]/;

  // // Critical non-English keywords for fallback (when franc fails)
  // const CRITICAL_NON_ENGLISH_KEYWORDS = [
  //   // Spanish
  //   'hola', 'gracias', 'por favor', 'sí', 'cómo', 'qué', 'español', 'señor', 'adiós',
  //   // French  
  //   'bonjour', 'merci', 'oui', 'non', 'comment', 'français', 'très', 'avec',
  //   // German
  //   'hallo', 'danke', 'bitte', 'nein', 'wie', 'deutsch', 'sehr', 'auch',
  //   // Italian
  //   'ciao', 'grazie', 'prego', 'come', 'italiano', 'bene', 'molto', 'sono',
  //   // Portuguese
  //   'olá', 'obrigado', 'obrigada', 'sim', 'não', 'português', 'muito', 'para',
  //   // Indonesian
  //   'selamat', 'terima kasih', 'bagaimana', 'dimana', 'dengan', 'untuk', 'datang', 'layanan', 'kami',
  //   // Dutch
  //   'hallo', 'dankje', 'alsjeblieft', 'hoe', 'wat', 'waar', 'nederlands',
  //   // Russian (transliterated common words)
  //   'spasibo', 'pozhaluysta', 'kak', 'chto', 'gde'
  // ];

  // /**
  //  * Enhanced language detection using Franc library with fallbacks
  //  */
  // const detectNonEnglishContent = (text: string, selectedLanguage: string): { 
  //   isValid: boolean; 
  //   detectedLanguage?: string; 
  //   confidence: 'high' | 'medium' | 'low';
  //   method: 'script' | 'franc' | 'keyword' | 'passed';
  // } => {
  //   const trimmedText = text.trim();

  //   // Skip validation if language is not English
  //   if (!['en', 'en_GB', 'en_US'].includes(selectedLanguage)) {
  //     return { isValid: true, confidence: 'high', method: 'passed' };
  //   }

  //   // 1. Quick filter: Check for non-Latin scripts (highest confidence)
  //   if (NON_LATIN_SCRIPTS.test(text)) {
  //     return {
  //       isValid: false,
  //       confidence: 'high',
  //       method: 'script'
  //     };
  //   }

  //   // 2. Use Franc for language detection (works well for longer text)
  //   try {
  //     const detectedLang = franc(text);

  //     // Franc returns language codes (eng, spa, fra, deu, etc.)
  //     // If it's confident and not English, block it
  //     if (detectedLang && detectedLang !== 'und' && detectedLang !== 'eng') {
  //       // Get confidence scores for better accuracy
  //       const allResults = francAll(text);
  //       const topResult = allResults[0];

  //       // If confidence is reasonable and it's clearly not English
  //       if (topResult && topResult[1] > 0.7) { // 70% confidence threshold
  //         return {
  //           isValid: false,
  //           detectedLanguage: detectedLang,
  //           confidence: 'high',
  //           method: 'franc'
  //         };
  //       }

  //       // Medium confidence detection
  //       if (topResult && topResult[1] > 0.4) { // 40% confidence threshold
  //         return {
  //           isValid: false,
  //           detectedLanguage: detectedLang,
  //           confidence: 'medium',
  //           method: 'franc'
  //         };
  //       }
  //     }
  //   } catch (error) {
  //     // Franc failed, continue to fallback methods
  //     console.warn('Franc detection failed:', error);
  //   }

  //   // 3. Fallback: Critical keyword detection for short text
  //   const words = trimmedText.toLowerCase().split(/\s+/);
  //   const foundNonEnglishWords = words.filter(word => 
  //     CRITICAL_NON_ENGLISH_KEYWORDS.includes(word.replace(/[.,!?;:'"()]/g, ''))
  //   );

  //   if (foundNonEnglishWords.length >= 1) {
  //     return {
  //       isValid: false,
  //       confidence: foundNonEnglishWords.length >= 2 ? 'high' : 'medium',
  //       method: 'keyword'
  //     };
  //   }

  //   // 4. Additional pattern checks for obvious cases
  //   const obviousPatterns = [
  //     /[¿¡]/,  // Spanish punctuation
  //     /[àèùêîôûçñáéíóúü]/i,  // Romance language accents
  //     /[ßäöü]/i,  // German characters
  //     /\b(le|la|les|du|de|des|der|die|das|el|la|los|las)\s+/i  // Articles
  //   ];

  //   for (const pattern of obviousPatterns) {
  //     if (pattern.test(text)) {
  //       return {
  //         isValid: false,
  //         confidence: 'medium',
  //         method: 'keyword'
  //       };
  //     }
  //   }

  //   // All checks passed - likely English
  //   return { isValid: true, confidence: 'high', method: 'passed' };
  // };







  /**
   * Enhanced WhatsApp message body validation with Franc language detection
   */
  const validateWhatsappBody = async (
    body: string,
    selectedLanguage: string,
    setWhatsappBodyError: (error: string | null) => void
  ): Promise<boolean> => {
    const trimmedBody = body.trim();

    // Basic validations (your existing logic)
    if (!trimmedBody) {
      setWhatsappBodyError("Message body is required.");
      return false;
    }


    if (Array.from(trimmedBody).length < 25) {
      setWhatsappBodyError("Message body must be at least 25 characters.");
      return false;
    }

    if (Array.from(trimmedBody).length > 1024) {
      setWhatsappBodyError("Message body must not exceed 1024 characters.");
      return false;
    }


    //   if (!await validateEnglishContent(trimmedBody, selectedLanguage, setWhatsappBodyError)) {

    //   return false;
    // }





    // All validations passed
    setWhatsappBodyError(null);
    return true;
  };


  // const validateEnglishContent = async (
  //   text: string,
  //   selectedLanguage: string,
  //   setError: (error: string | null) => void
  // ): Promise<boolean> => {
  //   if (!['en', 'en_GB', 'en_US'].includes(selectedLanguage)) {
  //     return true; // Not English, skip validation
  //   }

  //   const languageCheck = await detectNonEnglishContent(text, selectedLanguage);
  //   console.log("Selected language:", selectedLanguage);
  //   console.log("Language check result:", languageCheck);

  //   if (!languageCheck.isValid) {
  //       setError("This content appears to be in a non-English language. Please use English for English templates.");
  //     return false;
  //   }

  //   return true;
  // };



  /**
   * English content validation helper
   */
  // const validateEnglishContent = async (
  //   text: string,
  //   selectedLanguage: string,
  //   setError: (error: string | null) => void
  // ): Promise<boolean> => {
  //   // Clear any existing error first
  //   setError(null);

  //   if (!['en', 'en_GB', 'en_US'].includes(selectedLanguage)) {
  //     return true; // Not English, skip validation
  //   }

  //   try {
  //     const languageCheck = await detectNonEnglishContent(text, selectedLanguage);
  //     console.log("Selected language:", selectedLanguage);
  //     console.log("Language check result:", languageCheck);

  //     if (!languageCheck.isValid) {
  //       setError("This content appears to be in a non-English language. Please use English for English templates.");
  //       return false;
  //     }

  //     return true;
  //   } catch (error) {
  //     console.warn("Language validation error:", error);
  //     // If detection fails, allow the content to pass
  //     return true;
  //   }
  // };


  /**
   * Enhanced WhatsApp footer validation with Franc language detection
   */
  const validateWhatsappFooter = async (
    footer: string,
    selectedLanguage: string,
    setWhatsappFooterError: (error: string | null) => void
  ): Promise<boolean> => {
    // Footer is optional - empty footer is allowed
    if (!footer) {
      setWhatsappFooterError(null);
      return true;
    }

    const trimmedFooter = footer.trim();

    // Check character limit (60 characters max)
    if (Array.from(trimmedFooter).length > 60) {
      setWhatsappFooterError("Footer must not exceed 60 characters.");
      return false;
    }

    // Check for line breaks (not allowed in footer)
    if (trimmedFooter.includes('\n') || trimmedFooter.includes('\r')) {
      setWhatsappFooterError("Footer cannot contain line breaks.");
      return false;
    }

    // Based on Meta's official example: Plain text only (no emojis)
    const containsEmoji = (text: string): boolean => {
      for (let i = 0; i < text.length; i++) {
        const code = text.codePointAt(i);
        if (!code) continue;

        if (
          (code >= 0x1F600 && code <= 0x1F64F) || // emoticons
          (code >= 0x1F300 && code <= 0x1F5FF) || // symbols & pictographs
          (code >= 0x1F680 && code <= 0x1F6FF) || // transport & map
          (code >= 0x1F1E0 && code <= 0x1F1FF) || // flags (regional indicators)
          (code >= 0x2600 && code <= 0x26FF) || // misc symbols
          (code >= 0x2700 && code <= 0x27BF) || // dingbats
          (code >= 0xFE00 && code <= 0xFE0F) || // variation selectors
          (code >= 0x1F900 && code <= 0x1F9FF)    // supplemental symbols
        ) return true;

        if (code > 0xFFFF) i++; // handle surrogate pairs
      }
      return false;
    };

    if (containsEmoji(trimmedFooter)) {
      setWhatsappFooterError("Footer cannot contain emojis.");
      return false;
    }

    // Conservative approach: Check for formatting characters (not allowed)
    const formattingRegex = /[*_~`]/;
    if (formattingRegex.test(trimmedFooter)) {
      setWhatsappFooterError("Footer cannot contain formatting characters (* _ ~ `).");
      return false;
    }

    // Check for variables/placeholders (not allowed in footer)
    const variableRegex = /\{\{.*?\}\}/;
    if (variableRegex.test(trimmedFooter)) {
      setWhatsappFooterError("Footer cannot contain variables or placeholders.");
      return false;
    }

    //   // Language validation (only for English templates)
    //  if (selectedLanguage === 'en' || selectedLanguage === 'en_GB' || selectedLanguage === 'en_US' )  {
    //     const languageCheck = detectNonEnglishContent(trimmedFooter, selectedLanguage);

    //     // if (!languageCheck.isValid) {
    //     //   setWhatsappFooterError("Footer content appears to be in a non-English language. Please use English for English templates.");
    //     //   return false;
    //     // }
    //   }


    //   if (!await validateEnglishContent(trimmedFooter, selectedLanguage, setWhatsappFooterError)) {
    //   return false;
    // }

    setWhatsappFooterError(null);
    return true;
  };

  /**
   * Enhanced WhatsApp header text validation with Franc language detection
   */
  const validateHeaderText = async (
    headerText: string | null,
    selectedLanguage: string,
    setHeaderTextError: (error: string | null) => void
  ): Promise<boolean> => {
    const maxLength = 60;
    const variablePattern = /\{\{\d+\}\}/g;
    const formattingPattern = /[*_~`]/; // markdown symbols
    const newlinePattern = /\r|\n/;

    const containsEmoji = (text: string): boolean => {
      for (let i = 0; i < text.length; i++) {
        const code = text.codePointAt(i);
        if (!code) continue;

        if (
          (code >= 0x1F600 && code <= 0x1F64F) || // emoticons
          (code >= 0x1F300 && code <= 0x1F5FF) || // symbols & pictographs
          (code >= 0x1F680 && code <= 0x1F6FF) || // transport & map
          (code >= 0x2600 && code <= 0x26FF) || // misc symbols
          (code >= 0x2700 && code <= 0x27BF)      // dingbats
        ) return true;

        if (code > 0xffff) i++; // handle surrogate pairs
      }
      return false;
    };

    // ✅ Allow empty or null — header is optional
    if (!headerText || headerText.trim() === "") {
      setHeaderTextError(null);
      return true;
    }

    if (headerText.length > maxLength) {
      setHeaderTextError("Header text must be 60 characters or fewer.");
      return false;
    }

    if (newlinePattern.test(headerText)) {
      setHeaderTextError("Header text must be a single line.");
      return false;
    }

    if ((headerText.match(variablePattern) || []).length > 1) {
      setHeaderTextError("Only one variable (e.g., {{1}}) is allowed.");
      return false;
    }

    if (containsEmoji(headerText)) {
      setHeaderTextError("Emojis are not allowed in header text.");
      return false;
    }

    if (formattingPattern.test(headerText)) {
      setHeaderTextError("Formatting (bold, italics, etc.) is not allowed.");
      return false;
    }

    //   // Language validation (only for English templates)
    // if (selectedLanguage === 'en' || selectedLanguage === 'en_GB' || selectedLanguage === 'en_US' )  {
    //     const languageCheck = detectNonEnglishContent(headerText, selectedLanguage);


    //     // if (!languageCheck.isValid) {
    //     //   setHeaderTextError("Header content appears to be in a non-English language. Please use English for English templates.");
    //     //   return false;
    //     // }
    //   }



    //   if (!await validateEnglishContent(headerText, selectedLanguage, setHeaderTextError)) {
    //   return false;
    // }

    setHeaderTextError(null);
    return true;
  };


  // /**
  //    * Validates button text for WhatsApp templates
  //    */
  // const validateButtonText = (buttonText: string, buttonType: string, buttonIndex: number): boolean => {
  //   // COPY_CODE buttons have fixed text "Copy"
  //   if (buttonType === "COPY_CODE") {
  //     setButtonError(buttonIndex, "buttonText", null);
  //     return true;
  //   }

  //   const trimmedText = buttonText.trim();

  //   if (!trimmedText) {
  //     setButtonError(buttonIndex, "buttonText", "Button text is required.");
  //     return false;
  //   }

  //   if (trimmedText.length > 25) {
  //     setButtonError(buttonIndex, "buttonText", "Button text must not exceed 25 characters.");
  //     return false;
  //   }

  //   if (trimmedText.includes('\n') || trimmedText.includes('\r')) {
  //     setButtonError(buttonIndex, "buttonText", "Button text cannot contain line breaks.");
  //     return false;
  //   }


  //     // ✅ Allow only letters, numbers, and spaces
  // const allowedPattern = /^[A-Za-z0-9 ]+$/;
  // if (!allowedPattern.test(trimmedText)) {
  //   setButtonError(buttonIndex, "buttonText", "Button text can only contain letters, numbers, and spaces.");
  //   return false;
  // }

  //   setButtonError(buttonIndex, "buttonText", null); // Ensure error is cleared on success
  //   return true;
  // };


  //   /**
  //  * Enhanced WhatsApp button text validation with dl3 language detection
  //  */
  // const validateButtonText = async (
  //   buttonText: string, 
  //   buttonType: string, 
  //   buttonIndex: number,
  //   selectedLanguage: string,
  //   setButtonError: (index: number, field: string, error: string | null) => void
  // ): Promise<boolean> => {


  //   console.log("Button Text ENteringgggggggggggggggggggggggggggggggggggggggg");
  //   // COPY_CODE buttons have fixed text "Copy"
  //   if (buttonType === "COPY_CODE") {
  //     setButtonError(buttonIndex, "buttonText", null);
  //     return true;
  //   }

  //   const trimmedText = buttonText.trim();

  //   if (!trimmedText) {
  //     setButtonError(buttonIndex, "buttonText", "Button text is required.");
  //     return false;
  //   }

  //   if (trimmedText.length > 25) {
  //     setButtonError(buttonIndex, "buttonText", "Button text must not exceed 25 characters.");
  //     return false;
  //   }

  //   if (trimmedText.includes('\n') || trimmedText.includes('\r')) {
  //     setButtonError(buttonIndex, "buttonText", "Button text cannot contain line breaks.");
  //     return false;
  //   }

  //   // ✅ Allow only letters, numbers, and spaces
  //   const allowedPattern = /^[A-Za-z0-9 ]+$/;
  //   if (!allowedPattern.test(trimmedText)) {
  //     setButtonError(buttonIndex, "buttonText", "Button text can only contain letters, numbers, and spaces.");
  //     return false;
  //   }

  //   // // Language validation (only for English templates)
  //   // if(selectedLanguage === 'en' || selectedLanguage === 'en_GB' || selectedLanguage === 'en_US' )  {
  //   //   const languageCheck = detectNonEnglishContent(trimmedText, selectedLanguage);


  //   //   console.log('Button Language Check:', {
  //   //     text: trimmedText,
  //   //     result: languageCheck,
  //   //     selectedLanguage
  //   //   });
  //   //   // if (!languageCheck.isValid) {
  //   //   //   setButtonError(buttonIndex, "buttonText", "Button text appears to be in a non-English language. Please use English for English templates.");
  //   //   //   return false;
  //   //   // }
  //   // }




  // //   if (!await validateEnglishContent(trimmedText, selectedLanguage, setWhatsappFooterError)) {
  // //   // setButtonError(buttonIndex, "buttonText", "Button text appears to be in a non-English language. Please use English for English templates.");
  // //   return false;
  // // }


  // // if (!await validateEnglishContent(trimmedText, selectedLanguage, 
  // //   (errorMsg) => setButtonError(buttonIndex, "buttonText", errorMsg))) {
  // //   return false;
  // // }


  // const isEnglishValid = await validateEnglishContent(
  //   trimmedText,
  //   selectedLanguage,
  //   (errorMsg) => setButtonError(buttonIndex, "buttonText", errorMsg)
  // );

  // if (!isEnglishValid) {
  //   return false;
  // }




  //   if (trimmedText.length > 25) {
  //     setButtonError(buttonIndex, "buttonText", "Button text must not exceed 25 characters.");
  //     return false;
  //   }

  //   setButtonError(buttonIndex, "buttonText", null); // Ensure error is cleared on success
  //   return true;
  // };



  /**
   * Enhanced WhatsApp button text validation with language detection - CLEANED UP
   */
  const validateButtonText = async (
    buttonText: string,
    buttonType: string,
    buttonIndex: number,
    selectedLanguage: string,
    setButtonError: (index: number, field: string, error: string | null) => void
  ): Promise<boolean> => {

    console.log("Button Text Entering");

    // COPY_CODE buttons have fixed text "Copy"
    if (buttonType === "COPY_CODE") {
      setButtonError(buttonIndex, "buttonText", null);
      return true;
    }

    const trimmedText = buttonText.trim();

    // if (!trimmedText) {
    //   setButtonError(buttonIndex, "buttonText", "Button text is required.");
    //   return false;
    // }


    // NEW CODE:
    // Early return for empty text - don't show "required" error if button type is also empty
    if (!trimmedText) {
      // Only show required error if button type is selected but text is empty
      if (buttonType) {
        setButtonError(buttonIndex, "buttonText", "Button text is required.");
        return false;
      } else {
        // Both text and type are empty - clear error
        setButtonError(buttonIndex, "buttonText", null);
        return true;
      }
    }

    if (trimmedText.length > 25) {
      setButtonError(buttonIndex, "buttonText", "Button text must not exceed 25 characters.");
      return false;
    }

    if (trimmedText.includes('\n') || trimmedText.includes('\r')) {
      setButtonError(buttonIndex, "buttonText", "Button text cannot contain line breaks.");
      return false;
    }

    // Allow only letters, numbers, and spaces
    const allowedPattern = /^[A-Za-z0-9 ]+$/;
    if (!allowedPattern.test(trimmedText)) {
      setButtonError(buttonIndex, "buttonText", "Button text can only contain letters, numbers, and spaces.");
      return false;
    }

    // // Language validation - AWAIT the async function
    // const isEnglishValid = await validateEnglishContent(
    //   trimmedText,
    //   selectedLanguage,
    //   (errorMsg) => setButtonError(buttonIndex, "buttonText", errorMsg)
    // );

    // if (!isEnglishValid) {
    //   return false;
    // }

    // Clear error on success
    setButtonError(buttonIndex, "buttonText", null);
    return true;
  };


  /**
   * Validates Website URL for URL buttons
   */
  // const validateWebsiteUrl = (websiteUrl: string, buttonIndex: number): boolean => {
  //   const trimmedUrl = websiteUrl.trim();

  //   if (!trimmedUrl) {
  //     setButtonError(buttonIndex, "websiteUrl", "Website URL is required for URL buttons.");
  //     return false;
  //   }

  //   const urlPattern = /^https?:\/\/.+\..+/;
  //   if (!urlPattern.test(trimmedUrl)) {
  //     setButtonError(buttonIndex, "websiteUrl", "Please enter a valid URL starting with http:// or https://");
  //     return false;
  //   }

  //   if (trimmedUrl.includes(' ')) {
  //     setButtonError(buttonIndex, "websiteUrl", "URL cannot contain spaces.");
  //     return false;
  //   }

  //   setButtonError(buttonIndex, "websiteUrl", null); // Ensure error is cleared on success
  //   return true;
  // };
  const validateWebsiteUrl = (websiteUrl: string, buttonIndex: number): boolean => {
    console.log('Validating URL:', websiteUrl, 'for button index:', buttonIndex);
    const trimmedUrl = websiteUrl.trim();

    if (!trimmedUrl) {
      console.log('URL is empty');
      setButtonError(buttonIndex, "websiteUrl", "Website URL is required for URL buttons.");
      return false;
    }


    const urlPattern = /^(https:\/\/.+\..+|www\..+\..+)$/i;

    if (!urlPattern.test(trimmedUrl)) {
      console.log('URL pattern failed');
      setButtonError(
        buttonIndex,
        "websiteUrl",
        "Please enter a valid URL starting with https:// or beginning with www."
      );
      return false;
    }


    if (trimmedUrl.includes(' ')) {
      console.log('URL contains spaces');
      setButtonError(buttonIndex, "websiteUrl", "URL cannot contain spaces.");
      return false;
    }

    console.log('URL validation passed, clearing error');
    setButtonError(buttonIndex, "websiteUrl", null);
    return true;
  };
  /**
   * Validates phone number for PHONE_NUMBER buttons
   */
  const validateCallPhoneNumber = (phoneNumber: string, buttonIndex: number): boolean => {
    const trimmedPhone = phoneNumber.trim();

    if (!trimmedPhone) {
      setButtonError(buttonIndex, "callPhoneNumber", "Phone number is required for phone buttons.");
      return false;
    }



    const phonePattern = /^\d+$/; // ✅ only digits
    if (!phonePattern.test(trimmedPhone)) {
      setButtonError(buttonIndex, "callPhoneNumber", "Phone number can only contain digits.");
      return false;
    }


    const digitsOnly = trimmedPhone.replace(/\D/g, '');

    if (digitsOnly.length < 7) {
      setButtonError(buttonIndex, "callPhoneNumber", "Phone number must contain at least 7 digits.");
      return false;
    }

    if (digitsOnly.length > 15) {
      setButtonError(buttonIndex, "callPhoneNumber", "Phone number cannot exceed 15 digits.");
      return false;
    }



    setButtonError(buttonIndex, "callPhoneNumber", null); // Ensure error is cleared on success
    return true;
  };

  /**
   * Validates country code selection
   */
  const validateCountryCode = (countryCode: string, buttonIndex: number): boolean => {
    if (!countryCode || countryCode.trim() === "") {
      setButtonError(buttonIndex, "countryCode", "Country code is required for phone buttons.");
      return false;
    }

    setButtonError(buttonIndex, "countryCode", null); // Ensure error is cleared on success
    return true;
  };

  /**
   * Validates offer code for COPY_CODE buttons
   */
  const validateOfferCode = (offerCode: string, buttonIndex: number): boolean => {
    const trimmedCode = offerCode.trim();

    if (!trimmedCode) {
      setButtonError(buttonIndex, "offerCode", "Offer code is required for copy code buttons.");
      return false;
    }



    // ✅ New validation: allow only alphanumeric, dash, underscore
    const codePattern = /^[A-Za-z0-9_-]+$/;
    if (!codePattern.test(trimmedCode)) {
      setButtonError(buttonIndex, "offerCode", "Offer code can only contain letters, numbers, dashes, and underscores.");
      return false;
    }

    if (trimmedCode.length > 15) {
      setButtonError(buttonIndex, "offerCode", "Offer code must not exceed 15 characters.");
      return false;
    }

    if (trimmedCode.includes('\n') || trimmedCode.includes('\r')) {
      setButtonError(buttonIndex, "offerCode", "Offer code cannot contain line breaks.");
      return false;
    }

    setButtonError(buttonIndex, "offerCode", null); // Ensure error is cleared on success
    return true;
  };

  /**
   * Validates button type selection
   */
  const validateButtonType = (buttonType: string, buttonIndex: number): boolean => {
    if (!buttonType || buttonType.trim() === "") {
      setButtonError(buttonIndex, "buttonType", "Button type selection is required.");
      return false;
    }

    // const validTypes = ["URL", "PHONE_NUMBER", "COPY_CODE", "QUICK_REPLY"];
    // if (!validTypes.includes(buttonType)) {
    //   setButtonError(buttonIndex, "buttonType", "Invalid button type selected.");
    //   return false;
    // }

    setButtonError(buttonIndex, "buttonType", null); // Ensure error is cleared on success
    return true;
  };

  // /**
  //  * Validates entire button configuration
  //  */
  // const validateButtonsArray = (buttons: any[]): boolean => {

  //   console.log("Main Button Enteringgggggggggggggggggggggggggggggggggggggg");
  //   if (buttons.length > 3) {
  //     setButtonsError("Maximum 3 buttons are allowed per template.");
  //     return false;
  //   }

  //   let hasErrors = false;



  // // ✅ Extra validation: allow only one PHONE_NUMBER button
  // const phoneNumberButtons = buttons.filter(b => b.buttonType === "PHONE_NUMBER");
  // if (phoneNumberButtons.length > 1) {
  //   setButtonsError("Only one Phone Number button is allowed per template.");
  //   return false;
  // }


  //   // ✅ Allow only one COPY_CODE button
  // const copyCodeButtons = buttons.filter(b => b.buttonType === "COPY_CODE");
  // if (copyCodeButtons.length > 1) {
  //   setButtonsError("Only one Offer Code button is allowed per template.");
  //   return false;
  // }

  // // ✅ Allow maximum two URL buttons
  // const urlButtons = buttons.filter(b => b.buttonType === "URL");
  // if (urlButtons.length > 2) {
  //   setButtonsError("Maximum 2 URL buttons are allowed per template.");
  //   return false;
  // }

  //   buttons.forEach((button, index) => {
  //     // Only validate buttons that have content
  //     if (!button.buttonText.trim() && !button.buttonType) {
  //       clearButtonErrors(index); // Clear errors for empty buttons
  //       console.log("indexxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",index);
  //       return;
  //     }

  //     // Validate button type
  //     if (!validateButtonType(button.buttonType, index)) {
  //       hasErrors = true;
  //     }

  //     // Validate button text
  //     if (!validateButtonText(button.buttonText, button.buttonType, index, language, setButtonError)) {
  //       console.log("ENtering Button Texttttttttttttttttttttttttttttttttttttt")
  //       hasErrors = true;
  //     }




  //     // Validate specific fields based on button type
  //     switch (button.buttonType) {
  //       case "URL":
  //         if (!validateWebsiteUrl(button.websiteUrl, index)) {
  //           hasErrors = true;
  //         }
  //         break;

  //       case "PHONE_NUMBER":
  //         if (!validateCountryCode(button.countryCode, index)) {
  //           hasErrors = true;
  //         }
  //         if (!validateCallPhoneNumber(button.callPhoneNumber, index)) {
  //           hasErrors = true;
  //         }
  //         break;

  //       case "COPY_CODE":
  //         if (!validateOfferCode(button.copyOfferCode, index)) {
  //           hasErrors = true;
  //         }
  //         break;
  //     }
  //   });

  //   if (hasErrors) {
  //     setButtonsError("Please fix the button validation errors above.");
  //     return false;
  //   }

  //   setButtonsError(null);
  //   return true;
  // };

  /**
 * Validates entire button configuration - FIXED VERSION
 */
  const validateButtonsArray = async (buttons: any[]): Promise<boolean> => {
    console.log("Main Button Entering");

    if (buttons.length > 3) {
      setButtonsError("Maximum 3 buttons are allowed per template.");
      return false;
    }

    let hasErrors = false;

    // Extra validation: allow only one PHONE_NUMBER button
    const phoneNumberButtons = buttons.filter(b => b.buttonType === "PHONE_NUMBER");
    if (phoneNumberButtons.length > 1) {
      setButtonsError("Only one Phone Number button is allowed per template.");
      return false;
    }

    // Allow only one COPY_CODE button
    const copyCodeButtons = buttons.filter(b => b.buttonType === "COPY_CODE");
    if (copyCodeButtons.length > 1) {
      setButtonsError("Only one Offer Code button is allowed per template.");
      return false;
    }

    // Allow maximum two URL buttons
    const urlButtons = buttons.filter(b => b.buttonType === "URL");
    if (urlButtons.length > 2) {
      setButtonsError("Maximum 2 URL buttons are allowed per template.");
      return false;
    }

    // Rule: Quick Reply button texts must be unique
    const quickReplyButtons = buttons
      .filter(b => b.buttonType === "QUICK_REPLY" && b.buttonText.trim())
      .map(b => b.buttonText.trim().toLowerCase());

    const duplicateTexts = quickReplyButtons.filter(
      (text, index) => quickReplyButtons.indexOf(text) !== index ||
        quickReplyButtons.lastIndexOf(text) !== index
    );

    if (duplicateTexts.length > 0) {
      buttons.forEach((button, index) => {
        if (
          button.buttonType === "QUICK_REPLY" &&
          duplicateTexts.includes(button.buttonText.trim().toLowerCase())
        ) {
          setButtonError(index, "buttonText", "Quick reply button text must be unique.");
          hasErrors = true;
        }
      });
    }

    // Process buttons sequentially to handle async validation properly
    // Process buttons sequentially to handle async validation properly
    for (let index = 0; index < buttons.length; index++) {
      const button = buttons[index];

      // Check if button is completely empty (no text and no type selected)
      const isEmpty = !button.buttonText.trim() && !button.buttonType;

      if (isEmpty) {
        clearButtonErrors(index); // Clear errors for empty buttons
        console.log("index", index);
        continue;
      }

      // Validate button type
      if (!validateButtonType(button.buttonType, index)) {
        hasErrors = true;
      }

      // Check if this button already has a duplicate error set above
      const isDuplicate =
        button.buttonType === "QUICK_REPLY" &&
        duplicateTexts.includes(button.buttonText.trim().toLowerCase());

      if (isDuplicate) {
        hasErrors = true;
      } else {
        // Validate button text - AWAIT the async function
        const isTextValid = await validateButtonText(
          button.buttonText,
          button.buttonType,
          index,
          language,
          setButtonError
        );

        if (!isTextValid) {
          console.log("Entering Button Text validation error");
          hasErrors = true;
        }
      }

      // Validate specific fields based on button type
      switch (button.buttonType) {
        case "URL":
          if (!validateWebsiteUrl(button.websiteUrl, index)) {
            hasErrors = true;
          }
          break;

        case "PHONE_NUMBER":
          if (!validateCountryCode(button.countryCode, index)) {
            hasErrors = true;
          }
          if (!validateCallPhoneNumber(button.callPhoneNumber, index)) {
            hasErrors = true;
          }
          break;

        case "COPY_CODE":
          if (!validateOfferCode(button.copyOfferCode, index)) {
            hasErrors = true;
          }
          break;

        case "QUICK_REPLY":
          // No extra field validation needed — only buttonText is required
          break;
      }
    }


    if (hasErrors) {
      // setButtonsError("Please fix the button validation errors above.");
      return false;
    }

    setButtonsError(null);
    return true;
  };






















  /**
   * Validates SMS message body:
   * - Must not be empty
   */
  const validateSmsBody = (body: string): boolean => {
    if (!body.trim()) {
      setSmsBodyError("SMS body is required.");
      return false;
    }
    setSmsBodyError(null);
    return true;
  };

  /**
   * Validates start date:
   * - Must not be empty
   */
  const validateStartDate = (value: string): boolean => {
    if (!value) {
      setStartDateError("Start date is required.");
      return false;
    }
    setStartDateError(null);
    return true;
  };

  /**
   * Validates end date:
   * - Must not be empty
   * - Cannot be earlier than start date
   */
  // const validateEndDate = (value: string): boolean => {
  //   if (!value) {
  //     setEndDateError("End date is required.");
  //     return false;
  //   }
  //   if (new Date(campaignEndDate) < new Date(campaignStartDate)) {
  //     setEndDateError("End date cannot be earlier than start date.");
  //     return false;
  //   }
  //   setEndDateError(null);
  //   return true;
  // };

  const validateEndDate = (value: string): boolean => {
  if (!value) {
    setEndDateError("End date is required.");
    return false;
  }

  const parseDate = (dateStr: string) => {
    const [dd, mm, yyyy] = dateStr.split('/');
    return new Date(`${yyyy}-${mm}-${dd}`);
  };

  if (parseDate(value) < parseDate(campaignStartDate)) {
    setEndDateError("End date cannot be earlier than start date.");
    return false;
  }

  setEndDateError(null);
  return true;
};

  /**
   * Validates start time:
   * - Must not be null
   */
  const validateStartTime = (value: string | null): boolean => {
    if (!value) {
      setStartTimeError("Start time is required.");
      return false;
    }
    setStartTimeError(null);
    return true;
  };

  /**
   * Validates end time:
   * - Must not be null
   */
  const validateEndTime = (value: string | null): boolean => {
    if (!value) {
      setEndTimeError("End time is required.");
      return false;
    }
    // You can re-enable this logic later if you compare time strings
    // if (endTime <= startTime) {
    //   setEndTimeError("End time must be later than start time.");
    //   return false;
    // }
    setEndTimeError(null);
    return true;
  };

  /**
   * Validates language selection:
   * - Cannot be null or empty
   */
  const validateLanguage = (language: string | null): boolean => {
    if (!language || language.trim() === "") {
      setLanguageError("Please select a language.");
      return false;
    }
    setLanguageError(null);
    return true;
  };

  // /**
  //  * Validates text header input for WhatsApp template:
  //  * - Optional: Empty/null is allowed
  //  * - If provided:
  //  *   - Max 60 characters
  //  *   - Must be single line
  //  *   - No emojis
  //  *   - No formatting (bold, italics, etc.)
  //  *   - Only one variable (e.g., {{1}})
  //  */
  // const validateHeaderText = (headerText: string | null): boolean => {
  //   const maxLength = 60;
  //   const variablePattern = /\{\{\d+\}\}/g;
  //   const formattingPattern = /[*_~`]/; // markdown symbols
  //   const newlinePattern = /\r|\n/;

  //   const containsEmoji = (text: string): boolean => {
  //     for (let i = 0; i < text.length; i++) {
  //       const code = text.codePointAt(i);
  //       if (!code) continue;

  //       if (
  //         (code >= 0x1F600 && code <= 0x1F64F) || // emoticons
  //         (code >= 0x1F300 && code <= 0x1F5FF) || // symbols & pictographs
  //         (code >= 0x1F680 && code <= 0x1F6FF) || // transport & map
  //         (code >= 0x2600 && code <= 0x26FF) || // misc symbols
  //         (code >= 0x2700 && code <= 0x27BF)      // dingbats
  //       ) return true;

  //       if (code > 0xffff) i++; // handle surrogate pairs
  //     }
  //     return false;
  //   };

  //   // ✅ Allow empty or null — header is optional
  //   if (!headerText || headerText.trim() === "") {
  //     setHeaderTextError(null);
  //     return true;
  //   }

  //   if (headerText.length > maxLength) {
  //     setHeaderTextError("Header text must be 60 characters or fewer.");
  //     return false;
  //   }

  //   if (newlinePattern.test(headerText)) {
  //     setHeaderTextError("Header text must be a single line.");
  //     return false;
  //   }

  //   if ((headerText.match(variablePattern) || []).length > 1) {
  //     setHeaderTextError("Only one variable (e.g., {{1}}) is allowed.");
  //     return false;
  //   }

  //   if (containsEmoji(headerText)) {
  //     setHeaderTextError("Emojis are not allowed in header text.");
  //     return false;
  //   }

  //   if (formattingPattern.test(headerText)) {
  //     setHeaderTextError("Formatting (bold, italics, etc.) is not allowed.");
  //     return false;
  //   }

  //   setHeaderTextError(null);
  //   return true;
  // };


  /**
   * Validates phone number:
   * - Must be digits only
   * - Maximum length: 15
   */
  const validatePhoneNumber = (phone: string): boolean => {
    const digitOnlyPattern = /^\d+$/;

    if (!phone.trim()) {
      setPhoneNumberError("Phone number is required.");
      return false;
    }

    if (!digitOnlyPattern.test(phone)) {
      setPhoneNumberError("Phone number must contain only digits.");
      return false;
    }

    if (phone.length > 15) {
      setPhoneNumberError("Phone number must not exceed 15 digits.");
      return false;
    }

    setPhoneNumberError(null);
    return true;
  };

  /**
   * Validates time zone selection:
   * - Cannot be null or empty
   */
  const validateTimeZone = (value: string): boolean => {
    if (!value || value.trim() === "") {
      setTimeZoneError("Please select a time zone.");
      return false;
    }
    setTimeZoneError(null);
    return true;
  };

  /**
 * Enhanced clear function that can clear specific index or entire channel
 * - clearValidationErrors('whatsapp') → clears all WhatsApp errors
 * - clearValidationErrors('whatsapp', 5) → clears error for WhatsApp index 5
 * - clearValidationErrors() → clears all errors
 */
  const clearValidationErrors = (channel?: 'whatsapp' | 'sms', index?: number) => {
    if (!channel) {
      // Clear all errors (both channels)
      clearAllChannelErrors();
      return;
    }

    if (index !== undefined) {
      // Clear specific field error
      clearCountError(channel, index);
    } else {
      // Clear entire channel errors
      clearChannelErrors(channel);
    }
  };

  const validateDraftName = (name: string, draftNames: string[]): boolean => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setDraftNameError("Draft name is required.");
      return false;
    }

    // ✅ Check for duplicate (case-insensitive)
    if (draftNames.some(d => d.toLowerCase() === trimmedName.toLowerCase())) {
      setDraftNameError("Draft already exists.");
      return false;
    }

    setDraftNameError(null);
    return true;
  };





  return {
    // Existing validators
    validateCampaignName,
    validateFromCountry,
    validateToCountry,
    validateContent,
    validateWhatsappBody,
    validateSmsBody,
    validateStartDate,
    validateEndDate,
    validateStartTime,
    validateEndTime,
    validateLanguage,
    validatePhoneNumber,
    validateTimeZone,
    validateHeaderText,
    validateWhatsappFooter,
    clearValidationErrors,
    validateButtonsArray,
    validateButtonText,
    validateWebsiteUrl,
    validateCallPhoneNumber,
    validateCountryCode,
    validateOfferCode,
    validateButtonType,
    validateContentAlone,
    // New channel validation functions
    validateTravellerCount,
    validateChannelTravellerCounts,
    validateAllChannelData,
    clearChannelErrors,
    clearAllChannelErrors,
    hasChannelErrors,
    getCountError,
    setCountError,
    clearCountError,
    validateDraftName
    // validateDraftDataSilently, // API call function
  };
};